import axios from "axios";
import { Bell } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { FaSignOutAlt, FaUserCircle } from "react-icons/fa";
import { FiMenu } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import { useAdminAuth } from "../../../../context/AdminAuthContext";
import useIsDesktop from "../../../../hooks/useIsDesktop";

const StaffNavbar = ({ setIsSidebarOpen }) => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  // const {  } = useAdminAuth();
  const isDesktop = useIsDesktop();
  const profileBtnRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [newBookingCount, setNewBookingCount] = useState(0);
  const { admin, adminRole, logoutAdmin, isAdmin, isStaff } = useAdminAuth();

  const fetchNewBookingsCount = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/service-bookings/get-all/by-staff-id/${admin?.id}`,
      );
      const bookings = res?.data?.data || [];
      const currentTotal = bookings.length;

      const lastCountStr = localStorage.getItem("lastBookingCount");
      const lastCount = lastCountStr ? parseInt(lastCountStr, 10) : 0;

      const newCount = currentTotal - lastCount;
      setNewBookingCount(newCount > 0 ? newCount : 0);
    } catch (err) {
      console.error("Failed to fetch new bookings count", err);
      setNewBookingCount(0);
    }
  };

  const adminName = admin?.name || "User";
  const roleLabel = adminRole === "staff" && "staff member";

  // useEffect(() => {
  //   fetchNewBookingsCount();

  //   const interval = setInterval(fetchNewBookingsCount, 10000);
  //   return () => clearInterval(interval);
  // }, []);

  const handleLogout = async () => {
    const isLoggedOut = await logoutAdmin();
    if (isLoggedOut) navigate("/admin-login");
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleHamburgerClick = () => {
    // console.log("clicked hamburger");
    setIsSidebarOpen((prev) => {
      return !prev;
    });
  };
  const initials = adminName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        profileBtnRef.current &&
        !profileBtnRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNotificationClick = () => {
    if (!adminRole) return;

    localStorage.setItem(
      "lastBookingCount",
      localStorage.getItem("currentBookingCount") || "0",
    );

    setNewBookingCount(0);
    navigate("/bookings");
  };

  const profileRoute = isStaff ? "/staff-profile" : "/admin-profile";

  if (isStaff && !isDesktop) return null;

  return (
    <React.Fragment>
      <nav className="bg-[#FFFFFF] shadow-lg absolute right-0 top-0 left-0 flex-1 p-3 text-black z-50 h-[4rem]">
        <div className="px-3 mx-auto flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center">
            <button
              className="lg:hidden text-primaryTextColor"
              onClick={handleHamburgerClick}
              aria-label="Toggle sidebar"
            >
              <FiMenu size={22} />
            </button>
          </div>

          {/* Right Side: Bell + Profile */}
          <div className="flex items-center self-end gap-4">
            {/* Notification Bell */}
            {isAdmin && (
              <div className="relative">
                <button
                  onClick={handleNotificationClick}
                  className="relative p-2 rounded-full hover:bg-gray-100 transition"
                  aria-label="New Bookings"
                >
                  <Bell size={24} className="text-gray-700" />

                  {newBookingCount > 0 && (
                    <span className="absolute top-0 right-0 bg-red-500 text-white text-[0.6rem] rounded-full h-5 w-5 flex items-center justify-center shadow-md animate-pulse">
                      {newBookingCount > 99 ? "99+" : newBookingCount}
                    </span>
                  )}
                </button>
              </div>
            )}

            {/* Profile Dropdown */}
            <button
              ref={profileBtnRef}
              onClick={toggleDropdown}
              className="flex items-center gap-1"
              type="button"
            >
              <div className="relative">
                <svg width="0" height="0">
                  <linearGradient
                    id="icon-gradient"
                    x1="100%"
                    y1="0%"
                    x2="0%"
                    y2="100%"
                  >
                    <stop
                      stopColor="currentColor"
                      className="text-primaryColor"
                      offset="0%"
                    />
                    <stop
                      stopColor="currentColor"
                      className="text-primaryTextColor"
                      offset="100%"
                    />
                  </linearGradient>
                </svg>
                <div className="h-8 w-8 rounded-full bg-gradient-to-br overflow-hidden from-accentTextColor to-brandAccentColor flex items-center justify-center text-white font-semibold text-sm select-none">
                  {admin?.profilePicture ? (
                    <img
                      src={admin.profilePicture}
                      alt={adminName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    initials
                  )}
                </div>
              </div>

              <svg
                className={`w-2 h-2 transition-transform ${isDropdownOpen ? "rotate-180" : ""
                  }`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 10 6"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 4 4 4-4"
                />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div
                ref={dropdownRef}
                className="absolute right-5 top-14 w-52 bg-white rounded-xl shadow-xl border border-gray-100 z-50 animate-fade-in"
              >
                {/* Header */}
                <div className="border-b border-gray-100">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-primaryTextColor truncate">
                      {adminName}
                    </p>
                    <p className="text-xs text-primaryColor capitalize">{roleLabel}</p>
                  </div>
                </div>

                {/* Actions */}
                <ul className="text-sm py-2 text-primaryTextColor">
                  <li>
                    <Link
                      to={profileRoute}
                      onClick={() => setIsDropdownOpen(false)}
                      className="flex items-center px-4 py-2 gap-3 hover:bg-primaryFadedColor hover:text-accentTextColor hover:bg-brandAccentColor/30 transition"
                    >
                      <FaUserCircle size={16} />
                      Profile
                    </Link>
                  </li>

                  <li>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsDropdownOpen(false);
                      }}
                      className="w-full flex items-center px-4 py-2 gap-3 text-left hover:bg-red-50 text-red-600 transition"
                    >
                      <FaSignOutAlt size={16} />
                      Sign out
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </nav>
    </React.Fragment>
  );
};

export default StaffNavbar;
